/**
 * Inventory filter trees (FS-029).
 *
 * The server interprets one filter grammar — the same function a saved view
 * and a report go through. This module's only job is to *build* that grammar
 * from what the panel's controls are set to, and it is kept pure so the
 * building can be tested without a browser.
 *
 * Nothing here evaluates a filter. If this file ever starts deciding whether a
 * row matches, there are two interpreters again.
 */

export type FilterOp =
  | "eq"
  | "ne"
  | "gt"
  | "gte"
  | "lt"
  | "lte"
  | "in"
  | "not_in"
  | "contains"
  | "starts_with"
  | "ends_with"
  | "is_null"
  | "is_not_null"
  | "between"
  | "similar_to";

export interface FilterLeaf {
  field: string;
  op: FilterOp;
  value?: unknown;
}

export type FilterNode =
  | FilterLeaf
  | { all: FilterNode[] }
  | { any: FilterNode[] }
  | { not: FilterNode };

/** The server's cap, mirrored so the builder can stop offering "add group". */
export const MAX_DEPTH = 3;

/** Operators that take no value, so the builder hides the value input. */
export const VALUELESS_OPS: FilterOp[] = ["is_null", "is_not_null"];

/** Ageing bands, the same boundaries FS-057's reports use. */
export const AGE_BANDS: {
  key: string;
  label: string;
  min: number;
  max?: number;
}[] = [
  { key: "0-30", label: "0–30 days", min: 0, max: 30 },
  { key: "31-60", label: "31–60", min: 31, max: 60 },
  { key: "61-90", label: "61–90", min: 61, max: 90 },
  { key: "91-180", label: "91–180", min: 91, max: 180 },
  { key: "180+", label: "Over 180", min: 181 },
];

/**
 * The one-click filters, as a flag each.
 *
 * Toggles rather than a field/operator/value row, because "no photos" is a
 * question somebody asks every morning and should not cost three dropdowns.
 */
export const QUICK_FLAGS: { key: string; label: string; node: FilterNode }[] = [
  {
    key: "no_photos",
    label: "No photos",
    node: { field: "photo_count", op: "eq", value: 0 },
  },
  {
    key: "no_serial",
    label: "No serial",
    node: { field: "has_serial", op: "eq", value: false },
  },
  {
    key: "no_vin",
    label: "No VIN",
    node: { field: "has_vin", op: "eq", value: false },
  },
  {
    key: "unplaced",
    label: "Not in a yard",
    node: { field: "is_unplaced", op: "eq", value: true },
  },
  {
    key: "uninspected",
    label: "Never inspected",
    node: { field: "is_inspected", op: "eq", value: false },
  },
  {
    key: "hold_overdue",
    label: "Hold overdue",
    node: { field: "hold_overdue", op: "eq", value: true },
  },
];

export interface SimpleFilterState {
  category: string;
  statuses: string[];
  minPrice: string;
  maxPrice: string;
  dateFrom: string;
  dateTo: string;
  make: string;
  model: string;
  yearFrom: string;
  yearTo: string;
  ageBands: string[];
  quickFlags: string[];
  operationalStatus: string;
  conditionIssue: string;
  daysInStatusOver: string;
}

export const EMPTY_SIMPLE: SimpleFilterState = {
  category: "",
  statuses: [],
  minPrice: "",
  maxPrice: "",
  dateFrom: "",
  dateTo: "",
  make: "",
  model: "",
  yearFrom: "",
  yearTo: "",
  ageBands: [],
  quickFlags: [],
  operationalStatus: "",
  conditionIssue: "",
  daysInStatusOver: "",
};

function numeric(text: string): number | null {
  if (text.trim() === "") return null;
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Compile the simple controls into filter nodes.
 *
 * Selected age bands and quick flags are ORed within their own group and
 * ANDed with everything else — "aged 61–90 or over 180, and a John Deere" is
 * what a person means when they tick two bands, not "nothing".
 */
export function simpleToNodes(state: SimpleFilterState): FilterNode[] {
  const nodes: FilterNode[] = [];

  if (state.statuses.length === 1) {
    nodes.push({ field: "status", op: "eq", value: state.statuses[0] });
  } else if (state.statuses.length > 1) {
    nodes.push({ field: "status", op: "in", value: state.statuses });
  }

  if (state.category)
    nodes.push({ field: "category", op: "eq", value: state.category });

  const min = numeric(state.minPrice);
  const max = numeric(state.maxPrice);
  if (min !== null)
    nodes.push({ field: "purchase_price", op: "gte", value: min });
  if (max !== null)
    nodes.push({ field: "purchase_price", op: "lte", value: max });

  if (state.dateFrom)
    nodes.push({ field: "created_at", op: "gte", value: state.dateFrom });
  if (state.dateTo)
    nodes.push({ field: "created_at", op: "lte", value: state.dateTo });

  // Fuzzy, not exact: nobody spells Caterpillar the same way twice, and a make
  // typed into a filter box is not picked from a list.
  if (state.make.trim())
    nodes.push({ field: "make", op: "similar_to", value: state.make.trim() });
  if (state.model.trim())
    nodes.push({ field: "model", op: "contains", value: state.model.trim() });

  const yearFrom = numeric(state.yearFrom);
  const yearTo = numeric(state.yearTo);
  if (yearFrom !== null)
    nodes.push({ field: "year", op: "gte", value: yearFrom });
  if (yearTo !== null) nodes.push({ field: "year", op: "lte", value: yearTo });

  if (state.ageBands.length) {
    const bands = AGE_BANDS.filter((band) =>
      state.ageBands.includes(band.key),
    ).map<FilterNode>((band) =>
      band.max === undefined
        ? { field: "days_in_inventory", op: "gte", value: band.min }
        : {
            all: [
              { field: "days_in_inventory", op: "gte", value: band.min },
              { field: "days_in_inventory", op: "lte", value: band.max },
            ],
          },
    );
    if (bands.length === 1) nodes.push(bands[0]!);
    else if (bands.length > 1) nodes.push({ any: bands });
  }

  const flags = QUICK_FLAGS.filter((flag) =>
    state.quickFlags.includes(flag.key),
  ).map((flag) => flag.node);
  // ANDed, not ORed: ticking "no photos" and "no serial" asks for the machines
  // missing both, which is the work queue worth looking at first.
  nodes.push(...flags);

  if (state.operationalStatus)
    nodes.push({
      field: "operational_status",
      op: "eq",
      value: state.operationalStatus,
    });
  if (state.conditionIssue) {
    nodes.push({
      any: [
        {
          field: "primary_condition_issue",
          op: "eq",
          value: state.conditionIssue,
        },
        {
          field: "secondary_condition_issue",
          op: "eq",
          value: state.conditionIssue,
        },
      ],
    });
  }

  const daysInStatus = numeric(state.daysInStatusOver);
  if (daysInStatus !== null)
    nodes.push({
      field: "days_in_current_status",
      op: "gte",
      value: daysInStatus,
    });

  return nodes;
}

/** Depth of a node, counting groups. A bare leaf is 0. */
export function depthOf(node: FilterNode | null | undefined): number {
  if (!node || typeof node !== "object") return 0;
  if ("all" in node) return 1 + Math.max(0, ...node.all.map(depthOf));
  if ("any" in node) return 1 + Math.max(0, ...node.any.map(depthOf));
  if ("not" in node) return 1 + depthOf(node.not);
  return 0;
}

/** Whether a node has anything in it worth sending. */
export function isEmptyNode(node: FilterNode | null | undefined): boolean {
  if (!node || typeof node !== "object") return true;
  if ("all" in node) return node.all.every(isEmptyNode);
  if ("any" in node) return node.any.every(isEmptyNode);
  if ("not" in node) return isEmptyNode(node.not);
  return !node.field;
}

/**
 * Combine the simple controls and the advanced tree into one filter.
 *
 * Returns `null` when there is nothing to filter by, so the caller sends no
 * `filters` key at all rather than an empty group the server has to interpret.
 */
export function buildFilterTree(
  state: SimpleFilterState,
  advanced: FilterNode | null,
): FilterNode | null {
  const nodes = simpleToNodes(state);
  if (advanced && !isEmptyNode(advanced)) nodes.push(advanced);
  if (!nodes.length) return null;
  return nodes.length === 1 ? nodes[0]! : { all: nodes };
}

/** How many of the simple controls are set — the badge on the Filters button. */
/**
 * The panel's whole state: the simple controls, plus the three things that are
 * not filters — the search box, the sort, and the advanced tree.
 *
 * Lives here rather than beside the component so the component file exports
 * only components, which is what keeps fast refresh working.
 */
export interface InventoryFilterState extends SimpleFilterState {
  search: string;
  sortBy: string;
  sortDir: "asc" | "desc";
  /** The nested builder's tree, or null when nobody has opened it. */
  advanced: FilterNode | null;
}

export const EMPTY_FILTERS: InventoryFilterState = {
  ...EMPTY_SIMPLE,
  search: "",
  sortBy: "created_at",
  sortDir: "desc",
  advanced: null,
};

export function countActive(
  state: SimpleFilterState,
  advanced: FilterNode | null,
): number {
  return (
    simpleToNodes(state).length + (advanced && !isEmptyNode(advanced) ? 1 : 0)
  );
}
